import { useEffect, useState } from 'react';

export interface SubNavItem {
  id: string;
  label: string;
  count?: number;
}

/**
 * תת-תפריט אנכי של מסך ההגדרות (עיצוב v2): כרטיס צר בצד, בכל שורה נקודה
 * + שם, והפריט הפעיל ברקע כחול רך. הבחירה עצמה נשמרת אצל המסך (?tab=).
 * במסך צר התפריט הופך לשורה אופקית נגללת, כדי שלא ידחוף את התוכן למטה.
 */
const NARROW = '(max-width: 720px)';

function isRtl() {
  return typeof document === 'undefined' || document.documentElement.dir !== 'ltr';
}

function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW);
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

export function SettingsSubNav({ items, value, onChange }: {
  items: SubNavItem[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const narrow = useNarrow();

  return (
    <nav
      className="card"
      aria-label="תפריט הגדרות"
      style={narrow ? {
        flex: '1 1 100%', minWidth: 0, padding: 6, display: 'flex', gap: 2, overflowX: 'auto',
      } : {
        flex: '0 0 220px', maxWidth: '100%', padding: 8, position: 'sticky', top: 12,
        display: 'flex', flexDirection: 'column', gap: 2,
      }}
    >
      <div role="tablist" aria-orientation={narrow ? 'horizontal' : 'vertical'} style={{ display: 'contents' }}>
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(item.id)}
              onMouseEnter={() => setHover(item.id)}
              onMouseLeave={() => setHover(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, height: 38, padding: '0 12px',
                border: 0, borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13.5, textAlign: 'start', flexShrink: 0, whiteSpace: 'nowrap',
                background: active ? 'var(--accent-soft)' : hover === item.id ? 'var(--surface-2)' : 'transparent',
                color: active ? 'var(--accent-ink)' : 'var(--text-2)',
                fontWeight: active ? 600 : 400,
                // עיצוב v2: בריחוף הפריט זז 3px לכיוון התוכן (translateX שלילי ב-RTL).
                transform: hover === item.id && !narrow ? `translateX(${isRtl() ? -3 : 3}px)` : 'none',
                transition: 'background .2s ease, color .2s ease, transform .25s var(--ease-out)',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6, height: 6, borderRadius: 99, flexShrink: 0,
                  background: active ? 'var(--accent)' : 'var(--line-strong)',
                }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
              {typeof item.count === 'number' && (
                <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-xs)', color: active ? 'var(--accent-ink)' : 'var(--muted)', fontWeight: 500 }}>
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
